import { v4 as uuidv4 } from 'uuid';

import * as styles from './Navbar.css';
import type {
  TNavbarItemElement,
  TNavbarMenuBurgerContentElement,
  TNavbarMenuBurgerElement,
  TNavbarMenuListElement,
  TNavbarMenuListGroupElement,
} from './Navbar.type';
import type { NavbarSerializableNode } from './Serializer.types';

export function reorganizeNavbarMobileChildren(node: NavbarSerializableNode) {
  if ('text' in node || node.type !== 'navbar_menu') return node;

  const { content, ...rest } = node;
  // @ts-expect-error
  const listGroups = content?.flatMap((c) => c.content).filter(Boolean) as TNavbarMenuListGroupElement[];
  const items = listGroups?.flatMap((c) => c.content).filter(Boolean) as TNavbarItemElement[];

  // Set parentInMobile to true for all dropdown elements that are children of a navbar_item that has showOnMobile set to true
  for (const item of items) {
    if (
      (item.attrs?.type === 'dropdown' || (item.attrs?.type === 'user_button' && item.attrs?.style === 'dropdown')) &&
      item.content
    ) {
      for (const navbarItemContent of item.content) {
        if (navbarItemContent?.attrs) {
          navbarItemContent.attrs.parentInMobile = item.attrs?.showOnMobile;
        }
      }
    }
  }

  const showLeftMobile = items.filter(
    (c) => c.attrs?.showOnMobile && c.attrs?.mobileJustify === 'start',
  ) as TNavbarItemElement[];
  const showRightMobile = items.filter(
    (c) => c.attrs?.showOnMobile && (c.attrs?.mobileJustify === 'end' || !c.attrs?.mobileJustify),
  ) as TNavbarItemElement[];
  const showCenterMobile = items.filter(
    (c) => c.attrs?.showOnMobile && c.attrs?.mobileJustify === 'center',
  ) as TNavbarItemElement[];
  const hiddenItems = items
    .filter((c) => !c.attrs?.showOnMobile)
    .sort((a, b) => {
      // Sorting rules:
      // 1. Search should be at the top
      // 2. Sort action types -> top to bottom: profile, login, sign_up
      if (a.attrs?.type === 'search') return -1;
      if (b.attrs?.type === 'search') return 1;

      const aAction = a.attrs?.action;
      const bAction = b.attrs?.action;
      if (aAction === 'sign_up') return 1;
      if (bAction === 'sign_up') return -1;
      if (aAction === 'login') return 1;
      if (bAction === 'login') return -1;
      if (aAction === 'profile') return 1;
      if (bAction === 'profile') return -1;
      return 0;
    }) as TNavbarItemElement[];

  const burgerJustify = node.attrs?.burgerJustify || 'end';
  const burger: TNavbarMenuBurgerElement = {
    type: 'navbar_menu_burger',
    attrs: {
      id: uuidv4(),
      mobileJustify: burgerJustify,
      color: node.attrs?.burgerColor,
    },
  };

  const burgerContent: TNavbarMenuBurgerContentElement = {
    type: 'navbar_menu_burger_content',
    attrs: {
      id: uuidv4(),
      background: node.attrs?.burgerDropdownBackground,
    },
    content: hiddenItems,
  };

  const listGroupLeft: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      id: listGroups?.[0]?.attrs?.id || uuidv4(),
      type: 'left',
    },
    content: burgerJustify === 'start' ? [burger, ...showLeftMobile] : showLeftMobile,
  };
  const listGroupRight: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      id: listGroups?.[1]?.attrs?.id || uuidv4(),
      type: 'right',
    },
    content: burgerJustify === 'end' ? [...showRightMobile, burger] : showRightMobile,
  };
  const listGroupCenter: TNavbarMenuListGroupElement = {
    type: 'navbar_menu_list_group',
    attrs: {
      id: listGroups?.[2]?.attrs?.id || uuidv4(),
      type: 'middle',
    },
    content: showCenterMobile,
  };

  const addedMobileListMenu: TNavbarMenuListElement = {
    type: 'navbar_menu_list',
    attrs: {
      ...content?.[0]?.attrs,
      id: content?.[0]?.attrs?.id || uuidv4(),
      justify: 'center',
      height: 68,
      customAttributes: {
        className: styles.mobileOnly,
      },
    },
    content: [listGroupLeft, listGroupCenter, listGroupRight],
  };

  const originalListMenu = content
    .filter((c) => c.type === 'navbar_menu_list')
    .map((l) => {
      return {
        ...l,
        attrs: {
          ...l.attrs,
          customAttributes: {
            ...l.attrs?.customAttributes,
            className: styles.desktopOnly,
          },
        },
      } as TNavbarMenuListElement;
    });

  return {
    ...rest,
    content: [...originalListMenu, addedMobileListMenu, burgerContent],
  };
}
